import type StandardChart from '../../core/StandardChart';
import { type Option } from '../../core/option';
import { isArray, normalizeToArray } from '../../util/utils';
import { debug } from '../../util/debug';
import { isNumber } from '../../util/math';
import styles from './index.module.css';
import { util } from 'zrender';

interface DataItem {
  value: string | number;
  date: string | number;
  name: string;
  groupId?: string;
}

interface CustomOpt {
  watermarkFormatter?: (s: number) => string | number;
}

let onclick;

function setAnimation(option: Option, animate: boolean, animationDuration: number) {
  const updateFrequency = animate ? animationDuration : 0;
  option.xAxis = normalizeToArray(option.xAxis);
  option.xAxis.forEach(item => {
    item.animationDuration = updateFrequency;
    item.animationDurationUpdate = updateFrequency;
  });
  option.yAxis = normalizeToArray(option.yAxis);
  option.yAxis.forEach(item => {
    item.animationDuration = updateFrequency;
    item.animationDurationUpdate = updateFrequency;
  });
  option.series = normalizeToArray(option.series);
  option.series.forEach(item => {
    item.animationDuration = updateFrequency;
    item.animationDurationUpdate = updateFrequency;
    if (item.label) {
      item.label.valueAnimation = !!animate;
    }
  });
}

function findPosition(intervalHeight, y) {
  let i = 0;
  while (intervalHeight * i < y) {
    i++;
  }
  return i;
}

function isInBoundary(boundary, point) {
  const [x, y] = point;
  const [minX, minY, maxX, maxY] = boundary;
  return x > minX && y > minY && x < maxX && y < maxY;
}

function removeZrTextState(zrText) {
  zrText?._children?.[zrText._children.length - 1]?.removeState('dvEmphasis');
}

function checkValue(name, date, value) {
  const valueNum = Number(value);
  if (Number.isNaN(valueNum)) {
    console.warn(`${date}的数据项${name}的value: ${value}不合法，无法被转为数字`);
    return false;
  }
  return true;
}

function checkField(d: DataItem) {
  if (typeof d.name !== 'string') {
    console.warn('name should be string');
    return false;
  }
  if (!isNumber(d.date)) {
    console.warn('date should be number');
    return false;
  }
  const valueNum = Number(d.value);
  const isValueNaN = Number.isNaN(valueNum);
  if (isValueNaN) {
    console.warn(`${d.date}的数据项${d.name}的value: ${d.value}不合法，无法被转为数字`);
  }
  return !isValueNaN;
}

function checkData(data: DataItem[] | DataItem[][]) {
  if (isArray(data)) {
    if (data.length === 0) {
      console.warn("DynamicHistogramHelper's data should not be empty array");
      return false;
    }
    if (isArray(data[0])) {
      return (data as DataItem[][]).every(el => el.every(checkField));
    }
    return (data as DataItem[]).every(checkField);
  }
  console.warn("DynamicHistogramHelper's data should be array");
  return false;
}

function setXAxisMaxAndMin(displayData) {
  let maxValue;
  let minValue;
  const firstEl = displayData[0];
  if (isArray(firstEl)) {
    maxValue = firstEl[0].value;
    minValue = displayData[displayData.length - 1][0].value;
  } else {
    maxValue = firstEl.value;
    minValue = displayData[displayData.length - 1].value;
  }
  return [maxValue, minValue];
}
export class DynamicHistogramHelper<D extends DataItem = DataItem> {
  private _chart: StandardChart;

  private _data: D[];

  private _eventCallbacks: { eventName: string; callback: (...param: any) => void }[] = [];

  private _currentDate: number | string;

  private _updated = false;

  private _delayFnQueue = [];

  private _opt;

  private _hoverParams;

  private _lastOrdinalData = [];

  private _isDataValid = false;

  private animationDuration;

  constructor(chart: StandardChart, option: Option, data: D[], opt?: CustomOpt) {
    this._chart = chart;
    this._data = data;
    this._opt = opt;
    this._chart.play({ option });
    this._isDataValid = checkData(data);
    this.animationDuration = option.animationDuration;
    this._chart.on('dv:afterupdate', () => {
      if (!this._updated) {
        this._updated = true;
        this._delayFnQueue.forEach((fn: () => void) => fn());
        this._delayFnQueue = [];
        this.registerClickHandler();
      }
    });
  }

  getSortKey(option) {
    const barSeries = normalizeToArray(option.series).find(item => item.type === 'bar');

    return (barSeries.dvSortKey as string) || 'value';
  }

  getCurrentPeriodData(option, date, sortKey) {
    let inverse: boolean;
    const barSeries = normalizeToArray(option.series).find(item => item.type === 'bar');
    if (barSeries.dvSortType) {
      inverse = barSeries.dvSortType === 'asc';
    } else {
      inverse = barSeries.dvSortByAsc;
    }
    let optionData = this._data.filter(item => {
      if (isArray(item)) {
        return (item[0] as D).date === date;
      }
      return item.date === date;
    });

    // // 第一次播放
    // if (this._lastOrdinalData.length === 0) {
    //   optionData.sort((a: D, b: D) => {
    //     if (inverse) {
    //       return Number(a[sortKey]) - Number(b[sortKey]);
    //     }
    //     return Number(b[sortKey]) - Number(a[sortKey]);
    //   });
    //   this._lastOrdinalData = optionData.map(item => {
    //     if (isArray(item)) {
    //       return (item[0] as D).name;
    //     }
    //     return item.name;
    //   });
    //   // 非首次播放
    // } else {
    //   const tempOptionData = this._lastOrdinalData.reduce((prev: string[], cur: string) => {
    //     const t = optionData.find(d => {
    //       if (isArray(d)) {
    //         return (d[0] as D).name === cur;
    //       }
    //       return d.name === cur;
    //     });
    //     if (t === undefined) {
    //       // throw new Error(`${date}中缺少名称为${cur}的数据，请补齐`);
    //       return prev;
    //     }
    //     prev.push(t);
    //     return prev;
    //   }, []);

    //   optionData = tempOptionData;
    // }

    return optionData;
  }

  play(date: string | number, animate: boolean) {
    if (this._updated && this._isDataValid) {
      const option = this._chart.getModel().getOption();
      const yAxis = normalizeToArray(option.yAxis);
      const xAxis = normalizeToArray(option.xAxis);

      const sortKey = this.getSortKey(option);

      const optionData = this.getCurrentPeriodData(option, date, sortKey);

      // 显示的柱子的数量
      const displayNumber = (yAxis[0].max as number) + 1;
      // 显示的数据
      const displayData = this._data
        .filter(item => {
          if (isArray(item)) {
            return (item[0] as D).date === date;
          }
          return item.date === date;
        })
        .sort((a: D, b: D) => {
          if (isArray(a)) {
            return Number(b[0][sortKey]) - Number(a[0][sortKey]);
          }
          return Number(b[sortKey]) - Number(a[sortKey]);
        })
        .slice(0, displayNumber);
      if (displayData.length === 0) {
        throw new Error(`当前${date}无数据,请检查传入的date是否存在数据`);
      }
      // 比较最值和0的关系，设置x轴的min或max
      const [maxValue, minValue] = setXAxisMaxAndMin(displayData);
      xAxis[0].min = minValue > 0 ? 0 : undefined;
      xAxis[0].max = maxValue < 0 ? 0 : undefined;
      debug('optionData::', optionData);
      option.series?.forEach((el, i) => {
        if (el.type === 'bar') {
          const valueKey = el.dvValueKey || 'value';
          el.data = optionData.map(item => {
            if (isArray(item)) {
              const _item = item[i] as D;
              return {
                ..._item,
                value: [_item[valueKey], _item.name]
              };
            }
            return {
              ...item,
              value: [item[valueKey], item.name]
            };
          });
        }
      });
      option.yAxis.forEach(y => {
        y.max = Math.min(optionData.length - 1, displayNumber);
      });
      let watermarkFormatter = (time: number | string): number | string => time;
      if (this._opt?.watermarkFormatter) {
        watermarkFormatter = this._opt.watermarkFormatter;
      }
      option.graphic.elements[0].style.text = watermarkFormatter(date);
      setAnimation(option, animate, this.animationDuration);
      this._currentDate = date;
      this._chart.getECharts().setOption(option, true);
    } else {
      this._delayFnQueue.push(() => this.play(date, animate));
    }
  }

  on(eventName: string, callback: (param: any) => void) {
    this._eventCallbacks.push({
      eventName,
      callback
    });
  }

  off(eventName: string, callback: (param: any) => void) {
    const target = this._eventCallbacks.findIndex(item => {
      return item.eventName === eventName && item.callback === callback;
    });
    if (target > -1) {
      this._eventCallbacks.splice(target, 1);
    }
  }

  trigger(eventName: string, ...payload: any) {
    this._eventCallbacks.forEach(item => {
      if (item.eventName === eventName) {
        item.callback(...payload);
      }
    });
  }

  registerClickHandler() {
    const echarts = this._chart.getECharts();
    const ecModel = echarts.getModel();
    if (!ecModel) {
      return setTimeout(this.registerClickHandler.bind(this));
    }
    const gridModel = ecModel.getComponent('grid');
    const gridRect = gridModel.coordinateSystem.getRect();
    const gridRectY: number = gridRect.y;
    const gridRectX: number = gridRect.x;
    const gridRectWidth: number = gridRect.width;
    const gridRectHeight: number = gridRect.height;

    const zr = echarts.getZr();
    const yAxisModel = ecModel.getComponent('yAxis');
    const yAxisView = echarts.getViewOfComponentModel(yAxisModel);
    const barCount = yAxisModel.option.max + 1;
    const intervalHeight = gridRectHeight / barCount;
    const seriesModel = ecModel.getSeriesByType('bar')[0];
    const seriesModelData = seriesModel.getData();

    if (!onclick) {
      onclick = e => {
        const { offsetX, offsetY } = e;
        const yAxisZRText2 = yAxisView.group
          .childAt(0)
          .childAt(0)
          ._children.filter(el => el.anid.includes('label'));
        if (
          isInBoundary(
            [0, gridRectY, gridRectX + gridRectWidth, gridRectY + gridRectHeight],
            [offsetX, offsetY]
          )
        ) {
          const index = findPosition(intervalHeight, offsetY - gridRectY);
          yAxisZRText2.some((item, i) => {
            if (index - 1 === i) {
              this.trigger(
                'click',
                item._children[item._children.length - 1].style.text,
                this._currentDate,
                // model里的option不太准确，不建议使用
                seriesModelData.getItemModel(i).option.value[0],
                seriesModelData.getItemModel(i).option,
                item._children[item._children.length - 1]
              );
              return true;
            }
            return false;
          });
        }
      };
    } else {
      zr.off('click', onclick);
    }
    zr.on('click', onclick);
  }

  makeHover(containerDiv: HTMLDivElement, emphasisColor: string, emphasisBackgroundColor: string) {
    if (this._hoverParams === undefined) {
      this._hoverParams = {
        containerDiv,
        emphasisColor,
        emphasisBackgroundColor
      };
    }
    containerDiv.classList.add(styles.relative);
    containerDiv.querySelector(styles['dv-bar-shadow'])?.remove();
    const shadowDiv = document.createElement('div');
    shadowDiv.className = styles['dv-bar-shadow'];
    const cvsDiv = containerDiv.childNodes[0] as HTMLDivElement;
    const cvsDivWidth: number = cvsDiv.getBoundingClientRect().width;

    const echarts = this._chart.getECharts();
    const ecModel = echarts.getModel();
    const gridModel = ecModel.getComponent('grid');
    const gridRect = gridModel.coordinateSystem.getRect();
    const gridRectY: number = gridRect.y;
    const gridRectX: number = gridRect.x;
    const gridRectWidth: number = gridRect.width;
    const gridRectHeight: number = gridRect.height;

    const zr = echarts.getZr();
    const yAxisModel = ecModel.getComponent('yAxis');
    const yAxisView = echarts.getViewOfComponentModel(yAxisModel);
    const barCount = yAxisModel.option.max + 1;
    const intervalHeight = gridRectHeight / barCount;
    shadowDiv.setAttribute('style', `width: ${cvsDivWidth}px; height: ${intervalHeight}px`);

    const canvasDiv = containerDiv.querySelector('div');
    containerDiv.appendChild(shadowDiv);
    containerDiv.insertBefore(shadowDiv, canvasDiv);

    function handleMousemove(e) {
      const { offsetX, offsetY } = e;
      const yAxisZRText2 = yAxisView.group
        .childAt(0)
        .childAt(0)
        ._children.filter(el => el.anid.includes('label'));
      if (
        isInBoundary(
          [0, gridRectY, gridRectX + gridRectWidth, gridRectY + gridRectHeight],
          [offsetX, offsetY]
        )
      ) {
        const index = findPosition(intervalHeight, offsetY - gridRectY);
        shadowDiv.setAttribute(
          'style',
          `
            display: ${index ? 'block' : 'none'};
            width: ${cvsDivWidth}px;
            height: ${intervalHeight}px;
            top: ${(index - 1) * intervalHeight + gridRectY}px;
            background-color: ${emphasisBackgroundColor};
        `
        );
        yAxisZRText2.forEach((item, i) => {
          const span = item._children[item._children.length - 1];
          if (span) {
            span.states.dvEmphasis = {
              style: {
                fill: emphasisColor
              }
            };
            if (index - 1 === i) {
              span.useState('dvEmphasis');
            } else {
              span.removeState('dvEmphasis');
            }
          }
        });
      } else {
        shadowDiv.setAttribute('style', 'display: none;');
        yAxisZRText2.forEach(removeZrTextState);
      }
    }

    function handleMouseout() {
      const yAxisZRText2 = yAxisView.group.childAt(0).childAt(0)._children.slice(1);
      shadowDiv.setAttribute('style', 'display: none;');
      yAxisZRText2.forEach(removeZrTextState);
    }

    zr.off('mousemove', handleMousemove);
    zr.on('mousemove', handleMousemove);
    zr.off('mouseout', handleMouseout);
    zr.on('mouseout', handleMouseout);
  }

  resize() {
    if (this._hoverParams) {
      const { containerDiv, emphasisColor, emphasisBackgroundColor } = this._hoverParams;
      this.makeHover(containerDiv, emphasisColor, emphasisBackgroundColor);
      this.registerClickHandler();
    }
  }
}
